import { useCallback } from 'react';
import WorkspaceAPI from '../../workspace/WorkspaceAPI';
import _ from 'lodash';
import RightsApi from '../RightsAPI';
import { WorkspaceAPIResponse } from '../../workspace/WorkspaceAPI.model';
import { RightUserAPIResponse } from '../Rights.model';

const WORKSPACE_MANAGER = 'viewFlexeserveWorkspaceAdministrator';

export const hasRoleInWorkspace = (
  workspace: WorkspaceAPIResponse,
  rights: RightUserAPIResponse[],
  rightName: string
) => {
  if (!rights || rights.length === 0 || !workspace) {
    return false;
  }

  // Check if user has WorkspaceManager right for this workspace's scope
  const workspaceScope = `workspace:${workspace.id}`;
  return rights.some((rightData) => {
    // Check if scope matches the workspace or is master (*)
    const scopeMatches =
      rightData.scope === workspaceScope || rightData.scope === '*';
    // Check if the role contains the required right
    const hasRight = rightData.rights.includes(rightName);

    return scopeMatches && hasRight;
  });
};

export const userWorkspaceManagerRole = (
  workspace: WorkspaceAPIResponse,
  rights: RightUserAPIResponse[]
) => {
  return hasRoleInWorkspace(workspace, rights, WORKSPACE_MANAGER);
};

/**
 * Hook to check if the user has WorkspaceManager right in the main workspace
 * Fetches user rights and workspaces, then validates the permission
 */
export const useWorkspaceManagerRight = () => {
  const [triggerRights, { isLoading: isLoadingRights }] =
    RightsApi.endpoints.getMyRights.useLazyQuery();
  const [triggerWorkspaces, { isLoading: isLoadingWorkspaces }] =
    WorkspaceAPI.endpoints.getMyWorkspaces.useLazyQuery();

  const getPermission = useCallback(async () => {
    const { data: rightsResult } = await triggerRights();
    const { data: workspaces } = await triggerWorkspaces(undefined);
    const mainWorkspace = _.last(
      _.sortBy(workspaces, (workspace) => workspace.parentId === null)
    );
    if (!mainWorkspace) {
      return false;
    }
    const hasPermission = userWorkspaceManagerRole(
      mainWorkspace,
      rightsResult || []
    );

    return hasPermission;
  }, [triggerRights, triggerWorkspaces]);

  const isChecking = isLoadingRights || isLoadingWorkspaces;

  return {
    getPermission,
    isLoadingRights: isChecking,
  };
};
